// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import type { Facility } from '../types';
import { columnChart, type ColumnDatum } from '../components/charts';
import { bySector, histogram } from '../analysis';
import { OVER, UNDER, NEUTRAL, positionColour, STATE_ORDER } from '../sectors';
import { escapeHtml, formatCompact, formatNumber, formatPercent } from '../utils/format';
import { glossaryTerm } from '../glossary';

type SortKey = 'name' | 'company' | 'state' | 'covered' | 'baseline' | 'excess' | 'methaneShare';

export function renderExplorer(root: HTMLElement, ctx: ViewContext): void {
  const { facilities } = ctx.data;
  const sectors = bySector(facilities);

  let query = '';
  let stateFilter = 'all';
  let sectorFilter = 'all';
  let posFilter: 'all' | 'over' | 'under' = 'all';
  let sortKey: SortKey = 'covered';
  let sortDir: 'asc' | 'desc' = 'desc';
  let binFilter: { from: number; to: number } | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Explore every facility</h1>
      <p class="view-sub">
        All ${formatNumber(facilities.length)} Safeguard-covered facilities. Search, filter and sort; click any row to open
        it. The histogram shows how far ${glossaryTerm('excess', 'over or under baseline')} facilities landed — click a bar
        to filter the table to it.
      </p>
    </div>

    <div class="panel" style="margin-bottom:var(--space-lg)">
      <div class="panel-title">How far facilities landed from their baseline</div>
      <div class="panel-sub">Each facility's covered emissions as a percentage of its baseline. 100% is exactly on the cap; above is over. Click a bar to filter.</div>
      <div class="chart-scroll" data-role="hist"></div>
      <div data-role="binnote" class="zoom-hint"></div>
    </div>

    <div class="controls">
      <input class="text-input" data-role="q" type="search" placeholder="Search facility or company…" aria-label="Search" style="width:min(260px,50vw)" />
      <select data-role="state">
        <option value="all">All states</option>
        ${STATE_ORDER.map((s) => `<option value="${s}">${s}</option>`).join('')}
        <option value="MULTI">National / multi-state</option>
      </select>
      <select data-role="sector">
        <option value="all">All industries</option>
        ${sectors.map((s) => `<option value="${escapeHtml(s.key)}">${escapeHtml(s.label)}</option>`).join('')}
      </select>
      <div class="seg" role="group" data-role="pos">
        <button class="seg-btn" data-pos="all" aria-pressed="true">All</button>
        <button class="seg-btn" data-pos="over" aria-pressed="false">Over</button>
        <button class="seg-btn" data-pos="under" aria-pressed="false">Under</button>
      </div>
      <span class="field-label" data-role="count" style="margin-left:auto"></span>
    </div>

    <div class="table-scroll">
      <table class="data">
        <thead><tr>
          <th class="sortable" data-sort="name">Facility</th>
          <th class="sortable" data-sort="company">Company</th>
          <th class="sortable" data-sort="state">State</th>
          <th class="sortable t-num" data-sort="covered">Covered</th>
          <th class="sortable t-num" data-sort="baseline">Baseline</th>
          <th class="sortable t-num" data-sort="excess">vs base</th>
          <th class="sortable t-num" data-sort="methaneShare">Methane</th>
        </tr></thead>
        <tbody data-role="rows"></tbody>
      </table>
    </div>
  `;

  const histSlot = root.querySelector('[data-role="hist"]') as HTMLElement;
  const binNote = root.querySelector('[data-role="binnote"]') as HTMLElement;
  const rowsEl = root.querySelector('[data-role="rows"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;

  // Histogram over covered/baseline ratio (%), clamped 0–200+.
  const ratioPct = (f: Facility) => (f.baseline > 0 ? (f.covered / f.baseline) * 100 : null);
  const HIST_MIN = 0;
  const HIST_MAX = 200;
  const bins = histogram(facilities, ratioPct, 20, HIST_MIN, HIST_MAX);

  const drawHist = () => {
    histSlot.innerHTML = '';
    const data: ColumnDatum[] = bins.map((b) => {
      const mid = (b.from + b.to) / 2;
      const active = binFilter && Math.abs(binFilter.from - b.from) < 0.01;
      return {
        id: `${b.from}`,
        label: `${Math.round(b.from)}`,
        value: b.count,
        colour: active ? '#0f172a' : mid > 100 ? OVER : mid < 100 ? UNDER : NEUTRAL,
        tip: `${Math.round(b.from)}–${Math.round(b.to)}% of baseline\n${b.count} facilities`,
      };
    });
    histSlot.appendChild(
      columnChart(data, {
        width: 900,
        height: 240,
        labelEvery: 2,
        valueFormat: (v) => formatNumber(v),
        onClick: (id) => {
          const from = Number(id);
          const b = bins.find((x) => Math.abs(x.from - from) < 0.01);
          if (!b) return;
          binFilter = binFilter && Math.abs(binFilter.from - from) < 0.01 ? null : { from: b.from, to: b.to };
          draw();
        },
      }),
    );
    binNote.textContent = binFilter
      ? `Filtered to facilities at ${Math.round(binFilter.from)}–${Math.round(binFilter.to)}% of baseline. Click the bar again to clear.`
      : '100% is exactly on the baseline. The mass sits just above it — most facilities came in a little over.';
  };

  const filtered = (): Facility[] => {
    let out = facilities;
    if (query) {
      const q = query.toLowerCase();
      out = out.filter((f) => f.name.toLowerCase().includes(q) || f.company.toLowerCase().includes(q));
    }
    if (stateFilter !== 'all') out = out.filter((f) => f.state === stateFilter);
    if (sectorFilter !== 'all') {
      const label = sectors.find((s) => s.key === sectorFilter)?.label;
      out = out.filter((f) => f.sector === label);
    }
    if (posFilter === 'over') out = out.filter((f) => f.excess > 0.5);
    else if (posFilter === 'under') out = out.filter((f) => f.excess < -0.5);
    if (binFilter) {
      out = out.filter((f) => {
        const r = ratioPct(f);
        return r != null && r >= binFilter!.from && (binFilter!.to >= HIST_MAX ? true : r < binFilter!.to);
      });
    }
    const cmp = (a: Facility, b: Facility): number => {
      let av: number | string;
      let bv: number | string;
      if (sortKey === 'name' || sortKey === 'company' || sortKey === 'state') {
        av = a[sortKey];
        bv = b[sortKey];
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      }
      av = (a[sortKey] as number) ?? -Infinity;
      bv = (b[sortKey] as number) ?? -Infinity;
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    };
    return out.slice().sort(cmp);
  };

  const draw = () => {
    drawHist();
    const rows = filtered();
    countEl.textContent = `${formatNumber(rows.length)} of ${formatNumber(facilities.length)} facilities`;
    rowsEl.innerHTML = rows
      .slice(0, 400)
      .map(
        (f) => `<tr data-slug="${escapeHtml(f.slug)}">
          <td class="loc-cell">${escapeHtml(f.name)}</td>
          <td class="loc-cell">${escapeHtml(f.company)}</td>
          <td>${f.state === 'MULTI' ? '<span class="muted">multi</span>' : escapeHtml(f.state)}</td>
          <td class="t-num">${formatCompact(f.covered)}</td>
          <td class="t-num">${formatCompact(f.baseline)}</td>
          <td class="t-num" style="color:${positionColour(f.excess)}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</td>
          <td class="t-num">${f.methaneShare != null ? formatPercent(f.methaneShare, 0) : '—'}</td>
        </tr>`,
      )
      .join('');
    if (rows.length > 400) {
      rowsEl.innerHTML += `<tr style="cursor:default"><td colspan="7" class="muted" style="text-align:center">Showing the first 400 — narrow the filters to see more.</td></tr>`;
    }
    root.querySelectorAll('th[data-sort]').forEach((th) => {
      const k = th.getAttribute('data-sort');
      th.innerHTML = th.innerHTML.replace(/\s*[▲▼]$/, '') + (k === sortKey ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
    });
  };

  // Wiring.
  const qInput = root.querySelector('[data-role="q"]') as HTMLInputElement;
  let timer: number | undefined;
  qInput.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      query = qInput.value.trim();
      draw();
    }, 300);
  });
  root.querySelector('[data-role="state"]')?.addEventListener('change', (e) => {
    stateFilter = (e.target as HTMLSelectElement).value;
    draw();
  });
  root.querySelector('[data-role="sector"]')?.addEventListener('change', (e) => {
    sectorFilter = (e.target as HTMLSelectElement).value;
    draw();
  });
  root.querySelector('[data-role="pos"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-pos]');
    if (!btn) return;
    posFilter = btn.getAttribute('data-pos') as typeof posFilter;
    root.querySelectorAll('[data-pos]').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
    draw();
  });
  root.querySelectorAll('th[data-sort]').forEach((th) => {
    th.addEventListener('click', () => {
      const k = th.getAttribute('data-sort') as SortKey;
      if (k === sortKey) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = k;
        sortDir = k === 'name' || k === 'company' || k === 'state' ? 'asc' : 'desc';
      }
      draw();
    });
  });
  rowsEl.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-slug]');
    if (tr) ctx.openFacility(tr.getAttribute('data-slug') as string);
  });

  draw();
}
